import React, { useState, useEffect } from "react";

import './App.css';
import skills from './skills'
import experiences from './experiences'
import education from './education'
// import projects from './projects' // Commented out for Phase 6
import TextAnimation from './TextAnimation';
import ErrorBoundary from './ErrorBoundary';
import TechCarousel from './components/TechCarousel';
import SocialLinks from './components/SocialLinks';
import calculateDuration from './duration';

import { FaBars, FaTimes, FaMoon, FaSun } from "react-icons/fa";
import { IoMdSchool } from "react-icons/io";
import { MdWork } from "react-icons/md";
import { GiRobotGrab } from "react-icons/gi";

const PersonalWebsite = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  // Updated welcome text as requested
  const animatedText = "Welcome to my portfolio page!";

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "skills", "experiences", "education"];
      let current = "";

      // Check each section to see which one is currently visible
      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const navHeight = 60; // Approximate nav height
          // Consider section active if its top is within viewport and bottom is visible
          if (rect.top <= navHeight + 100 && rect.bottom >= navHeight) {
            current = section;
          }
        }
      });

      // Special handling for the last section (education)
      const educationElement = document.getElementById("education");
      if (educationElement) {
        const rect = educationElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        // If education section is visible or we are near the bottom of the page, highlight education
        if (rect.top <= windowHeight * 0.7 || window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
          current = "education";
        }
      }

      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = (section) => {
    setActiveSection(section);
    setIsMenuOpen(false);
    const element = document.getElementById(section);
    if (element) {
      const navHeight = document.querySelector("nav").offsetHeight;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ErrorBoundary>
    <div className={`min-h-screen font-sans ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <nav className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-md fixed w-full z-50`}>
        <div className="container mx-auto px-6 py-3 md:flex md:justify-between md:items-center">
          <div className="flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src="/Robot.192x192.png" 
              alt="Robot Icon" 
              className={`${isDarkMode ? 'text-white invert' : 'text-gray-800'} h-8 w-8 mr-2`}
            />
            <button className={`${isDarkMode ? "text-white hover:text-gray-600" : "text-gray-800 hover:text-gray-400"} text-xl font-bold lg:text-2xl`} onClick={() => navigateTo("home")}>
              Saleh Teymouri
            </button>
          </div>
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full mr-2 ${isDarkMode ? 'bg-yellow-400 text-gray-900' : 'bg-gray-200 text-gray-600'}`}
              >
                {isDarkMode ? <FaSun /> : <FaMoon />}
              </button>
              <button
                type="button"
                className={`${isDarkMode ? 'text-white hover:text-gray-600' : 'text-gray-500 hover:text-gray-800'}`}
                aria-label="toggle menu"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
              </button>
            </div>
          </div>
          <div className={`md:flex items-center ${isMenuOpen ? 'block w-1/2' : 'hidden left-0'} md:relative absolute top-full right-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} md:bg-transparent`}>
            <div className="flex flex-col md:flex-row md:mx-6">
              {/* Updated navigation to exclude projects and contact */}
              {["home", "skills", "experiences", "education"].map((item) => (
                <button
                  key={item}
                  className={`my-2 md:text-sm lg:text-lg ${activeSection === item ? 'text-indigo-500 font-semibold' : isDarkMode ? 'text-white' : 'text-gray-700'} font-medium hover:text-indigo-500 md:mx-2 md:my-0 capitalize`}
                  onClick={() => navigateTo(item)}
                >
                  {item}
                </button>
              ))}
            </div>
            <button
              onClick={toggleDarkMode}
              className={`mt-4 md:mt-0 p-2 rounded-full ${isDarkMode ? 'bg-yellow-400 text-gray-900' : 'bg-gray-200 text-gray-600'} hidden md:block`}
            >
              {isDarkMode ? <FaSun /> : <FaMoon />}
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-16">
        <section id="home" className="relative flex items-center justify-center">
          <div className="absolute inset-0 z-0 brightness-50 blur-50">
            <img
              src="/Molg_Arms_03_2024.jpg"
              alt="Robotics arms background"
              className="w-full h-full object-cover filter brightness-50 blur-50"
            />
          </div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <img
            className="w-40 h-40 rounded-full mx-auto mb-4 mt-4 border-4 border-white shadow-lg"
              src="/pic.jpg"
              alt="Saleh Teymouri"
            />
            <h1 className="text-4xl font-bold mb-2 text-white">Saleh Teymouri</h1>
            <p className="text-xl mb-4 text-gray-300">Robotics DevOps Engineer</p>
            <div className="h-16 mb-4 mt-4">
            <TextAnimation text={animatedText} />
            </div>
            
            {/* Add social links to home section */}
            <SocialLinks isDarkMode={isDarkMode} variant="home" />
            
            <div className="text-center mt-8 h-16">
              <button
                className={`${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white px-4 py-2 rounded-md transition duration-300`}
              >
                <a href="/Teymouri.pdf" download>
                Download Resume
                </a>
              </button>
            </div>
            <p className="max-w-6xl mx-auto mb-10 text-gray-200">
              Passionate about building intelligent robotic systems, I hold a Master's in Mechanical Engineering from Lehigh University, with a focus on Robotics.              Currently a Robotics Systems Engineer at Dexterity, Inc., I have nearly 4 years of experience in designing, deploying, and managing complex systems.              My expertise spans robotic arms, mobile robotics, mechatronics, C++, Python, and automation tools like Ansible and Terraform.              <br /><br />              Here are the technologies I used during my career.
            </p>
            
            {/* Technology Carousel */}
            <TechCarousel isDarkMode={isDarkMode} />

          </div>
        </section>

        <section id="skills" className="container mx-auto px-6 py-8">
          <h2 className="text-xl font-bold md:text-2xl mb-6 flex items-center">
            <GiRobotGrab size={30} className="mr-2" />Technical Skills
            </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {skills.map((skill, index) => (
              <div 
                key={index} 
                className={`flex flex-col items-center justify-center shadow-md p-6 border border-gray-200 rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}
              >
                <h3 className="text-md text-center font-semibold mb-4">{skill.title}</h3>
                <ul className="flex flex-wrap justify-center text-gray-500">
                  {skill.technologies.map((tech, index) => (
                    <li 
                      key={index} 
                      className={`${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-600'} mr-2 mb-2 py-1 px-2 rounded-lg text-center`}
                    >
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section id="experiences" className="container mx-auto px-6 py-8">
          <h2 className="text-xl font-bold md:text-2xl mb-6 flex items-center">
            <MdWork size={24} className="mr-2" /> Work Experience
          </h2>
          <div className="space-y-6">
            {experiences.map((exp, index) => (
              <div 
                key={index} 
                className={`rounded-lg shadow-md p-6 border border-gray-200 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} transition duration-300`}
              >
                <div className="md:flex md:justify-between mb-4 hidden md:block">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">
                      <a 
                        href={exp.link} 
                        target="_blank" 
                        rel="noreferrer" 
                        className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-500' : 'text-indigo-600 hover:text-indigo-800'} underline transition duration-300`}
                      >
                        {exp.company}
                      </a>
                    </h2>
                    <h3 className="text-xl font-semibold">{exp.title}</h3>
                  </div>
                  <div className="text-right">
                    <p className="mb-1">{exp.location}</p>
                    <p>{calculateDuration(exp.period)} | {exp.period}</p>
                  </div>
                </div>
                <div className="md:hidden">
                  <h2 className="text-2xl font-bold mb-1">
                    <a 
                      href={exp.link} 
                      target="_blank" 
                      rel="noreferrer" 
                      className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-500' : 'text-indigo-600 hover:text-indigo-800'} underline transition duration-300`}
                    >
                      {exp.company}
                    </a>
                  </h2>
                  <h3 className="text-xl font-semibold mb-1">{exp.title}</h3>
                  <p className="mb-1">{exp.location}</p>
                  <p>{calculateDuration(exp.period)} | {exp.period}</p>
                </div>
                <div className="lg:grid lg:grid-cols-2 lg:gap-4">
                  <ul className="list-disc pl-4">
                    {exp.descriptions.slice(0, Math.ceil(exp.descriptions.length / 2)).map((desc, index) => (
                      <li key={index}>{desc}</li>
                    ))}
                  </ul>
                  <ul className="list-disc pl-4">
                    {exp.descriptions.slice(Math.ceil(exp.descriptions.length / 2)).map((desc, index) => (
                      <li key={index}>{desc}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="education" className="container mx-auto px-6 py-8">
          <h2 className="text-xl font-bold md:text-2xl mb-6 flex items-center">
            <IoMdSchool size={24} className="mr-2" /> Education
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {education.map((edu, index) => (
              <div 
                key={index} 
                className={`rounded-lg shadow-md p-6 border border-gray-200 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} transition duration-300`}
              >
                <div className="flex justify-between mb-4">
                  <h3 className="text-xl font-semibold">{edu.degree}</h3>
                  <p className="">
                    {edu.year}
                    <span className="mx-1">|</span>
                    {edu.location}
                  </p>
                </div>
                <h2 className="text-2xl font-bold mb-4">
                  <a 
                    href={edu.link} 
                    target="_blank" 
                    rel="noreferrer" 
                    className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-500' : 'text-indigo-600 hover:text-indigo-800'} underline transition duration-300`}
                  >
                    {edu.school}
                  </a>
                </h2>
                {edu.gpa && (
                  <p className="mb-4">
                    GPA: {edu.gpa}
                  </p>
                )}
                <h4 className="font-semibold mb-2 mt-4">Key Courses:</h4>
                <ul className="list-disc pl-4">
                  {edu.courses.map((course, index) => (
                    <li key={index}>{course}</li>
                  ))}
                </ul>
                {edu.researchAreas && (
                  <ul className="list-disc pl-4 mt-4">
                    <li>Research Areas: {edu.researchAreas.join(', ')}</li>
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 
          Projects and Contact sections are temporarily commented out
          and will be completed in future phases 
        */}
      </main>

      <footer className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'} py-4`}>
        <div className="container mx-auto px-6 text-center">
          <p>© {new Date().getFullYear()} Saleh Teymouri. All rights reserved.</p>
          {/* Add social links to footer */}
          <div className="mt-4">
            <SocialLinks isDarkMode={isDarkMode} variant="footer" />
          </div>
        </div>
      </footer>
    </div>
    </ErrorBoundary>
  );
};

export default PersonalWebsite;
